import { inject, Injectable, Signal, signal } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { StaticNavigationItems } from '@views/common/models/navigation-roaster.model';
import { ProfessionalSection } from '@views/common/models/routes.model';
import { Station } from '@vueling-ng/vy-cdk-searcher';
import { AvatarInput } from '@vueling-ng/vy-ds/src/lib/components/globals/avatar';
import { MenuHorizontalItem } from '@vueling-ng/vy-ds/src/lib/components/globals/menu-horizontal';
import { SegmentedControlsModel } from '@vueling-ng/vy-ds/src/lib/components/globals/segmented-controls';
import { TableConfig } from '@vueling-ng/vy-ds/src/lib/components/globals/table';
import { ProfessionalRoute } from 'app/core/common/models/professionals-route.config';
import { AnalyticsDataService } from 'app/services/external/analytics/analytics.service';
import { PageViewEventFields } from 'app/services/external/analytics/models/analytics.config';
import { SessionOidcService } from 'app/services/external/auth-oidc/session-oidc.service';
import { NavigationItems } from 'app/shared/models/navigation-item.model';
import { StateManagerService } from 'app/shared/state/state-manager.service';
import { BookingType } from 'app/views/booking-list/models/booking-type';
import { environment } from 'environments/environment';
import { catchError, Observable, of, OperatorFunction, Subject, takeUntil, tap } from 'rxjs';
import { LinksMenu, LinksMenuItemId } from '../models/booking-list-links-menu.enum';
import { BookingListViewModel, EmptyBooking, EmptyBookingListViewModel } from '../models/booking-list.interface';
import { defaultHeaders, TableDataProperty } from '../models/booking-table-header.model';
import {
  Booking,
  BookingsParams,
  BookingsRequest,
  defaultPageSize,
  defaultSortCriteria,
  EmptyBookingsResponse,
  Filters,
  SortCriteria
} from '../models/bookings.interface';
import { QuickFilters, QuickFiltersValue } from '../models/filters.model';
import { BookingsResponse } from './../models/bookings.interface';
import { BookingListApiService } from './booking-list-api.service';
import { BookingListFilterService } from './booking-list-filter.service';

@Injectable({
  providedIn: 'root'
})
export class BookingListService {
  private readonly _stateManagerService = inject(StateManagerService);
  private readonly _bookingListFilterService = inject(BookingListFilterService);
  private readonly _bookingListApiService = inject(BookingListApiService);
  private readonly _translateService = inject(TranslateService);
  private readonly _router = inject(Router);
  private readonly _analyticsDataService = inject(AnalyticsDataService);
  private readonly _sessionOidcService = inject(SessionOidcService);

  private readonly currentViewModel = signal<BookingListViewModel>(EmptyBookingListViewModel);
  private readonly allBookingsResponse = signal<BookingsResponse>(EmptyBookingsResponse);
  private readonly bookingsResponse = signal<BookingsResponse>(EmptyBookingsResponse);
  private readonly tableHeaders = signal<Record<string, TableDataProperty>[]>(defaultHeaders);

  public readonly pageSize = defaultPageSize;
  private currentSortCriteria: SortCriteria = defaultSortCriteria;

  private readonly _cancelPreviousRequest$ = new Subject<void>();

  public updateBookings(params: BookingsParams): Observable<BookingsResponse> {
    const { pageNumber, pageSize = this.pageSize, sortCriteria, filters } = params;

    return this.loadBookings(pageNumber, pageSize, sortCriteria, filters).pipe(
      tap((bookings) => {
        this.bookingsResponse.set(bookings);
        this.buildViewModelByBookingType('Standard');
      })
    );
  }

  public updateCreditBookings(params: BookingsParams): Observable<BookingsResponse> {
    const { pageNumber, pageSize = this.pageSize, sortCriteria, filters } = params;

    return this.loadCreditBookings(pageNumber, pageSize, sortCriteria, filters).pipe(
      tap((bookings) => {
        this.bookingsResponse.set(bookings);
        this.buildViewModelByBookingType('WithCredit');
      })
    );
  }

  public getAllBookings(): Observable<BookingsResponse> {
    return this.loadBookings().pipe(
      tap((bookings) => {
        this.allBookingsResponse.set(bookings);
      })
    );
  }

  public getAllCreditBookings(): Observable<BookingsResponse> {
    return this.loadCreditBookings().pipe(
      tap((bookings) => {
        this.allBookingsResponse.set(bookings);
      })
    );
  }

  public setTableHeaders(newHeaders: Record<string, TableDataProperty>[]): void {
    this.tableHeaders.set(newHeaders);
  }

  private loadBookings(
    pageNumber = 1,
    pageSize?: number,
    sortCriteria: SortCriteria = this.currentSortCriteria,
    filters: Filters = this._bookingListFilterService.getCurrentFilters()()
  ): Observable<BookingsResponse> {
    const state = this._stateManagerService.getState()();
    const apiFilters: Filters = { ...filters };

    this.resolveFilters(apiFilters, state.stations!);

    this.currentSortCriteria = this.resolveSortCriteria(sortCriteria);

    const request = this.buildRequestParams(pageNumber, pageSize, apiFilters);

    this._cancelPreviousRequest$.next();

    return this._bookingListApiService
      .loadBookingList(request)
      .pipe(takeUntil(this._cancelPreviousRequest$), this.catchError());
  }

  private loadCreditBookings(
    pageNumber = 1,
    pageSize?: number,
    sortCriteria: SortCriteria = this.currentSortCriteria,
    filters: Filters = this._bookingListFilterService.getCurrentFilters()()
  ): Observable<BookingsResponse> {
    const apiFilters: Filters = { ...filters };

    this.currentSortCriteria = this.resolveSortCriteria(sortCriteria);
    const request = this.buildRequestParams(pageNumber, pageSize, apiFilters);

    this._cancelPreviousRequest$.next();
    return this._bookingListApiService
      .loadBookingsListCreditShell(request)
      .pipe(takeUntil(this._cancelPreviousRequest$), this.catchError());
  }

  private resolveFilters(filters: Filters, stations: Station[]): void {
    filters.departureCode = this.getStationCodeFromStationName(filters.departureCode, stations);
    filters.returnCode = this.getStationCodeFromStationName(filters.returnCode, stations);
    if (filters.departureDate) {
      filters.departureDate = this.parseDate(filters.departureDate);
    }
    if (filters.returnDate) {
      filters.returnDate = this.parseDate(filters.returnDate);
    }
  }

  private buildRequestParams(pageNumber: number, pageSize?: number, apiFilters?: Filters): BookingsRequest {
    const request: BookingsRequest = {
      organizationCode: this._sessionOidcService.currentSessionOidc().userData.organizationCode,
      page: pageNumber.toString(),
      pageSize: pageSize?.toString(),
      orderField: this.currentSortCriteria.column,
      order: this.currentSortCriteria.direction,
      filters: apiFilters
    };
    return request;
  }

  private catchError(): OperatorFunction<BookingsResponse, BookingsResponse> {
    return catchError(() => {
      return of(EmptyBooking);
    });
  }

  public currentViewModelBooking(): Signal<BookingListViewModel> {
    return this.currentViewModel.asReadonly();
  }

  public getBookingsToExport(): Booking[] {
    return this.allBookingsResponse().bookings;
  }

  public getTableHeaders(): Record<string, TableDataProperty>[] {
    return this.tableHeaders();
  }

  public handleAnalyticsEvent(segmentedControl: SegmentedControlsModel): void {
    const sendDigitalData =
      segmentedControl.value === QuickFiltersValue.FlownQuickFilters ||
      segmentedControl.value === QuickFiltersValue.CancelledQuickFilters;

    if (sendDigitalData) {
      this.sendDigitalData(segmentedControl.value as QuickFilters);
    }
  }

  private sendDigitalData(filter: QuickFilters): void {
    const path = this._router.url.split('/')[1];
    const requestSnapshot: ProfessionalRoute = {
      section: ProfessionalSection,
      path: path,
      subSection: path
    };

    const pageViewEventFields: PageViewEventFields = {
      route: requestSnapshot,
      filter: filter
    };

    this._analyticsDataService.trackEvent('page_view', pageViewEventFields);
  }

  private buildViewModelByBookingType(bookingType: BookingType): void {
    const appState = this._stateManagerService.getState()();
    const isBookingCreditFly = bookingType === 'WithCredit';
    const viewModelByBooking: BookingListViewModel = {
      userName: appState.user!.userName,
      linksMenu: LinksMenu,
      bookingsTableData: this.bookingsResponse().bookings
        ? ({
            headerData: this.tableHeaders(),
            bodyData: this.bookingsResponse().bookings.map((booking) => {
              const recordLocatorInfo = isBookingCreditFly
                ? {
                    recordLocator: booking.recordLocator
                  }
                : {
                    recordLocator: booking.recordLocator,
                    gdsRecordLocator: booking.gdsRecordLocator,
                    channelType: booking.channelType,
                    currency: booking.currency
                  };

              const commonFields = {
                status: booking.status,
                currency: booking.currency,
                passengerCount: booking.passengerCount
              };

              return {
                recordLocatorInfo,
                ...(!isBookingCreditFly && {
                  route: this.getRouteLabels(booking.origin, booking.destination, appState.stations!),
                  departureDate: booking.departureDate,
                  returnDate: booking.returnDate
                }),
                ...(isBookingCreditFly && {
                  availableCredit: booking.availableCredit?.toString() ?? '',
                  usedCredit: booking.usedCredit?.toString() ?? ''
                }),
                ...commonFields
              };
            })
          } as TableConfig)
        : undefined,
      paginationInfo: {
        currentPage: this.bookingsResponse()?.page,
        totalPages: this.bookingsResponse()?.totalPages
      },
      totalBookingsFound: this.bookingsResponse()?.totalRequest,
      horizontalMenuItems: this.getHorizontalMenuItems(appState.language!),
      avatar: this.getAvatarInput(appState.user!.userName)
    };
    this.currentViewModel.set(viewModelByBooking);
  }

  private getHorizontalMenuItems(language: string): MenuHorizontalItem[] {
    return [
      { ...StaticNavigationItems.bookings, id: LinksMenuItemId.Bookings },
      {
        ...StaticNavigationItems.invoices,
        id: LinksMenuItemId.Invoices,
        href: environment.configurations.newInvoiceConfig.url
      },
      {
        ...StaticNavigationItems.groups,
        id: LinksMenuItemId.Groups,
        href: `${environment.groupsBaseUrl}${language}/GroupSearch`
      },
      { ...StaticNavigationItems.agency_credit, id: LinksMenuItemId.AgencyCredit },
      {
        ...StaticNavigationItems.cac,
        id: LinksMenuItemId.CAC,
        href: this._translateService.instant(NavigationItems.Cac.url!) as string
      },
      {
        ...StaticNavigationItems.manage_account,
        id: LinksMenuItemId.ManageAccount,
        href: `${environment.micewebBaseUrl}${language}/Edit`
      }
    ];
  }

  private getAvatarInput(username: string): AvatarInput {
    return {
      title: {
        firstLetter: username.substring(0, username.indexOf(' ')),
        secondLetter: username.substring(username.indexOf(' ') + 1) ?? ''
      }
    };
  }

  private getRouteLabels(originCode: string, destinationCode: string, stations: Station[]): string | undefined {
    const originLabel = stations.find((station) => station.stationCode === originCode)?.shortName;
    const destinationLabel = stations.find((station) => station.stationCode === destinationCode)?.shortName;

    if (!originLabel || !destinationLabel) {
      return undefined;
    }

    return `${originLabel} - ${destinationLabel}`;
  }

  private getStationCodeFromStationName(code: string | undefined, stations: Station[] | undefined): string | undefined {
    return stations?.find((station) => station.fullName === code)?.stationCode ?? code;
  }

  private resolveSortCriteria(sortCriteria: SortCriteria): SortCriteria {
    return sortCriteria.direction ? sortCriteria : defaultSortCriteria;
  }

  private parseDate(date: string): string {
    const [day, month, year] = date.split('/');
    return `${year}-${month}-${day}`;
  }
}
